import React, { useState } from "react";
import { useHistory } from "react-router";
import { useJwtCookie } from "../../hooks/useJwtCookie";
import { Routes } from "../../root/routes";
import { PageLoading } from "../../components";
import { MultiFactorAuthSetupPage } from "./MultiFactorAuthSetupPage";
import { AuthPageWrapper } from "../../components/layout/AuthPageWrapper";
import { MultiFactorAuthInput } from "../../components/organisms/auth/MultiFactorAuthInput";
import { useActionRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { Callout, CalloutVariant } from "../../components/Callout";
import { Error } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { GlobalStyles, Typography } from "@mui/material";
import { useEffectOnce } from "react-use";
import {
  BEACON_RESET_CSS,
  configureBeacon,
  destroyBeacon,
  initBeacon,
  setIdentity,
} from "../../util/beacon";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { UserDetailsDto } from "../../../common/service/server/dto/UserDto";
import { selfActions } from "../../../common/reducers/selfSlice";

const MFA_COOKIE_NAME = "polarisMFAInstance";

const useStyles = makeStyles({
  callout: {
    marginLeft: "43px",
  },
});

export const MultiFactorAuthPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { token, data: jwt } = useJwtCookie(MFA_COOKIE_NAME);
  const dispatch = useDispatch();

  useEffectOnce(() => {
    destroyBeacon();
    initBeacon();
    configureBeacon();
    if (jwt?.email) {
      setIdentity({
        email: jwt.email,
      });
    }
  });

  const [networkError, setNetworkError] = useState(false);
  const [wrongCodeError, setWrongCodeError] = useState(false);
  const [maxAttemptsError, setMaxAttemptsError] = useState(false);
  const [validateMfaCodeLoading, setValidateMfaCodeLoading] = useState(false);
  const [isNewMfa] = useLocalStorage("polarisNewMFA", "false");
  const queryClient = useQueryClient();

  const { fetch: validateMfaCode, request: validateMfaCodeRequest } = useActionRequest({
    notifyError: false,
    api: async (values) => {
      setCreateMfaChallengeError(false);
      setWrongCodeError(false);
      setNetworkError(false);
      setMaxAttemptsError(false);
      setValidateMfaCodeLoading(true);
      return Api.login.validateMfaCode(values);
    },
    onSuccess: async (payload: any) => {
      if (payload?.success) {
        setValidateMfaCodeLoading(false);
        localStorage.setItem("polarisNewMFA", "false");
        await queryClient.invalidateQueries(["self"]);
        const user = await Api.user.getSelf();
        if (user) {
          dispatch(selfActions.set(user as UserDetailsDto));
        }
        history.push(Routes.root);
      } else {
        setValidateMfaCodeLoading(false);
        setWrongCodeError(true);
      }
    },
    onError: (_state, e) => {
      if ((e as any)?.response?.status == 400) {
        setWrongCodeError(true);
      } else if ((e as any)?.response?.status == 429) {
        setMaxAttemptsError(true);
      } else {
        setNetworkError(true);
      }
      setValidateMfaCodeLoading(false);
    },
  });

  const [createMfaChallengeError, setCreateMfaChallengeError] = useState(false);
  const { fetch: createMfaChallenge, request: createMfaChallengeRequest } = useActionRequest({
    notifyError: false,
    api: async (values) => {
      setCreateMfaChallengeError(false);
      setWrongCodeError(false);
      setNetworkError(false);
      return Api.login.createMfaChallenge((values as any).token, (values as any).phone);
    },
    onSuccess: (payload: any) => {
      if (!payload?.success) {
        setCreateMfaChallengeError(true);
      }
    },
    onError: (_state, e) => {
      if ((e as any)?.response?.status == 429) {
        setMaxAttemptsError(true);
      } else {
        setCreateMfaChallengeError(true);
      }
    },
  });

  if (!jwt) {
    history.push(Routes.auth.login);
    return <PageLoading />;
  }

  if (!jwt.phone || isNewMfa == "true") {
    return (
      <MultiFactorAuthSetupPage
        token={token}
        jwt={jwt}
        validateMfaCode={(opts: { code: string; phone: string }) =>
          validateMfaCode({
            token,
            code: opts.code,
            phone: opts.phone,
          })
        }
        validateMfaCodeRequest={validateMfaCodeRequest}
        validateMfaCodeLoading={validateMfaCodeLoading}
        wrongCodeError={wrongCodeError}
        createMfaChallenge={createMfaChallenge}
        createMfaChallengeRequest={createMfaChallengeRequest}
        createMfaChallengeError={createMfaChallengeError || networkError}
        maxAttemptsError={maxAttemptsError}
      />
    );
  }

  let pageTitle = "Authenticate Your Account";

  return (
    <AuthPageWrapper
      navLinkBack={() => history.push(Routes.login)}
      navLinkBackLabel="Back to login"
      panelWidthStyle="600px"
    >
      <GlobalStyles styles={BEACON_RESET_CSS} />
      <div className="fill align-center">
        <img src="/assets/images/nav-icon-truevault.png" width={"40px"} />
      </div>
      <Typography color="primary" fontWeight="bold" className="text-center fill my-sm text-t5">
        {pageTitle}
      </Typography>

      {networkError && (
        <Callout
          icon={Error}
          variant={CalloutVariant.Secondary}
          title="System currently unable to send a code for authentication"
          className="my-std w-100"
        >
          <p className={classes.callout}>
            Please{" "}
            <a
              href="#"
              onClick={() => {
                (window as any).Beacon("open");
              }}
            >
              contact support
            </a>{" "}
            for assistance.
          </p>
        </Callout>
      )}

      {maxAttemptsError && (
        <Callout
          icon={Error}
          variant={CalloutVariant.Secondary}
          title="Max validation attempts reached, please wait 10 minutes and try again."
          className="my-std w-100"
        />
      )}

      <MultiFactorAuthInput
        phone={jwt.phone}
        wrongCodeError={wrongCodeError}
        validateMfaCode={(code: string) =>
          validateMfaCode({
            token,
            code,
          })
        }
        resendCode={() => createMfaChallenge({ phone: jwt.phone, token })}
        isLoading={validateMfaCodeLoading}
      />
    </AuthPageWrapper>
  );
};
